import * as Apollo from "@apollo/client";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import React, { ReactNode, useImperativeHandle } from "react";
import { usePaginatedQuery } from "@/hooks";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useTranslation } from "react-i18next";
import { CircleLoader } from "./loaders";
import { cn } from "@/lib/utils";

/**
 * This component holds all UI for Pagination. It calls the pagination hook and passes the dataSlice to the child component
 * @param { props, children }_ Query to use for pagination and array passed as children to child Component
 * @param ref - Ref to trigger handleFilterSubmit from parent component
 * @returns Pagination component
 */
function InnerPagination<TEdge, TData, TVariables extends Apollo.OperationVariables>(
  { props, children, ref }: PaginationWrapperProps<TEdge, TData, TVariables> & { ref?: React.Ref<{ triggerHandleFilterSubmit: (_newVariables: Partial<TVariables>) => void; getData?: () => TEdge[]; getLoading?: () => boolean; getSubscribeToMore?: any }> }
) {

  const { queryDocument, paginationName, reversePagination, className, fetchPolicy, initialFetchPolicy } = props;
  const options: UseGeneratedQueryOptions<TData, TVariables> = { queryDocument };

  const {
    loading,
    dataSlice,
    handleNextPage,
    subscribeToMore,
    handlePreviousPage,
    handleDisplayAmount,
    handleFilterSubmit,
    loadingMoreData,
    displayAmount,
    showingFrom,
    showingTo,
    rowsFetched,
  } = usePaginatedQuery<TEdge, TData, TVariables>(options, paginationName, reversePagination, fetchPolicy, initialFetchPolicy);

  // Hooks
  const { t } = useTranslation(["tables"]);

  useImperativeHandle(ref, () => ({
    triggerHandleFilterSubmit(newVariables: Partial<TVariables>) {
      handleFilterSubmit(newVariables);
    },
    getData() {
      return dataSlice; // Ensure that this returns the current slice of data
    },
    getLoading() {
      return loading || loadingMoreData;
    },
    getSubscribeToMore: subscribeToMore,
  }));

  // Rest of your component
  return (
    <div className="flex flex-col justify-between w-full">
      {/* Table goes here */}
      <div>
        {children(dataSlice, loading || loadingMoreData, displayAmount, subscribeToMore) as ReactNode}
      </div>

      {/* Pagination buttons */}
      <div className={cn("select-none flex items-center pt-2 h-full space-x-2", className)}>
        <Button
          size="sm"
          variant={"outline"}
          onClick={handlePreviousPage}
        >
          <ChevronLeftIcon className="icon-md" />
        </Button>

        <Button
          size="sm"
          variant={"outline"}
          disabled={loading || loadingMoreData}
          onClick={handleNextPage}
        >
          {
            loadingMoreData ?
              <CircleLoader
                visible={true}
                height={26}
                width={26}
                strokeWidth={4}
                color="#EE5300"
                secondaryColor="#EE5300"
                ariaLabel='oval-loading'
              /> :
              <ChevronRightIcon className="icon-md" />
          }
        </Button>

        <div className="flex items-center space-x-2 w-full">
          {/* Display Amount Toggle */}

          <Select
            name={"displayAmount"}
            key={"displayAmount"}
            defaultValue={"10"}
            onValueChange={(value) => {
              handleDisplayAmount(parseInt(value ?? ""));
            }}
            required={true}
          >
            <SelectTrigger
              className="min-w-[50px] max-h-[32px] select-none cursor-pointer dark:bg-mfdarklight dark:placeholder:text-gray-200 outline-hidden border border-slate-200 max-w-[70px] rounded-md p-1 dark:border-gray-300 dark:text-gray-300"
            >
              <SelectValue placeholder={"Display Amount"} />
            </SelectTrigger>
            <SelectContent>
              {
                [5, 10, 25, 50].map((displayAmount, index: number) => {
                  return (
                    <SelectItem
                      key={`${displayAmount}-option-${index}`}
                      value={displayAmount.toString()}>
                      {displayAmount.toString()}
                    </SelectItem>
                  );
                })}
            </SelectContent>
          </Select>

          <div className="hidden md:flex flex-col">
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              {
                t("tables:pagination.fetched", { amount: rowsFetched })
              }
            </p>

            <p className="text-gray-600 dark:text-gray-300 text-sm">
              {
                t("tables:pagination.showing", { from: showingFrom, to: showingTo })
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Ref forwarding is used to pass the ref from the parent component to the child component
export const Pagination = InnerPagination as <TEdge, TData, TVariables extends Apollo.OperationVariables>(
  _p: PaginationWrapperProps<TEdge, TData, TVariables> &
  {
    ref?: React.Ref<{
      triggerHandleFilterSubmit:
      (_newVariables: Partial<TVariables>) => void,
      getData?: () => TEdge[],
      getLoading?: () => boolean,
      getSubscribeToMore?: any;//SubscribeToMoreFn<TData, TVariables>,
    }>
  }
) => React.ReactElement;
